import { tryParse } from '@ps/web-core-utils';
import type { Client, StompHeaders } from '@stomp/stompjs';
import { mapKeys } from 'lodash';
import type { Observer } from 'rxjs';
import { Observable } from 'rxjs';
import { filter, finalize, share, switchMap } from 'rxjs/operators';

import type { connect } from './connect';
import { ConnectionState } from './enums/ConnectionState';
import type { Message } from './types';

const subscriptions: Record<string, Observable<Message<unknown>>> = {};

const subscription = <TMessage extends Message<unknown>>(
  client: Client,
  destination: string,
  headers: StompHeaders
) =>
  new Observable((messages: Observer<TMessage>) => {
    const stompSub = client.subscribe(
      destination,
      (message) =>
        messages.next({
          headers: message.headers,
          body: tryParse(message.body)
        } as TMessage),
      mapKeys(headers, (value, key) => key.toLowerCase())
    );

    // TODO global cypress types
    // if (integrationTesting) {
    //   set(window, ['__STARS_SUBSCRIPTIONS__', destination], stompSub.id);
    // }

    return () =>
      client.connected && !!client.subscriptions?.[stompSub.id] && stompSub.unsubscribe();
  });

export const subscribe = <TMessage extends Message<unknown>>(
  destination: string,
  headers: StompHeaders,
  connection: ReturnType<typeof connect>
) => {
  if (!subscriptions[destination]) {
    subscriptions[destination] = connection.pipe(
      filter(({ state }) => state === ConnectionState.CONNECTED),
      switchMap(({ client }) => subscription<TMessage>(client as Client, destination, headers)),
      finalize(() => delete subscriptions[destination]),
      share()
    );
  }

  return subscriptions[destination] as Observable<TMessage>;
};
